import { useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
    getNotificationDisplayTimestamp,
    QueueFilter,
} from "../../constants";
import { formatNotificationLocaleDateTime } from "../helpers";
import "./NotificationsList.scss";

/**
 * Formats a display name as "Last, First". Returns an empty string when both parts are missing
 * or whitespace-only.
 *
 * @param {unknown} lastName
 * @param {unknown} firstName
 * @returns {string}
 */
function formatLastFirstName(lastName, firstName) {
    const last =
        lastName != null && String(lastName).trim() !== ""
            ? String(lastName).trim()
            : "";
    const first =
        firstName != null && String(firstName).trim() !== ""
            ? String(firstName).trim()
            : "";

    if (!last && !first) {
        return "";
    }
    if (last && first) {
        return `${last}, ${first}`;
    }
    return last || first;
}

/**
 * Column label for the sent/scheduled timestamp column based on the active queue tab.
 *
 * @param {string | null} queueFilter {@link QueueFilter} value (`null` is All).
 * @returns {string}
 */
function getSentOrScheduledTimestampColumnLabel(queueFilter) {
    if (queueFilter === QueueFilter.SENT) {
        return "Sent At";
    }
    if (
        queueFilter === QueueFilter.PENDING ||
        queueFilter === QueueFilter.ERRORS
    ) {
        return "Scheduled For";
    }
    return "Scheduled/Sent";
}

/** Static column metadata: stable key, header label, cell formatter. */
const STATIC_DATA_COLUMNS = [
    {
        key: "client",
        header: "Client",
        format: (row) => row.client,
    },
    {
        key: "template",
        header: "Template",
        format: (row) => row.template,
    },
    {
        key: "type",
        header: "Type",
        format: (row) => row.type,
    },
    {
        key: "plan",
        header: "Plan",
        format: (row) => row.plan,
    },
    {
        key: "recipientId",
        header: "Member ID",
        format: (row) => row.recipientId,
    },
    {
        key: "memberDisplay",
        header: "Member Name",
        format: (row) =>
            formatLastFirstName(row.memberLastName, row.memberFirstName),
    },
    {
        key: "eligibilityType",
        header: "Eligibility Type",
        format: (row) => row.eligibilityType,
    },
    {
        key: "network",
        header: "Network",
        format: (row) => row.network,
    },
    {
        key: "pcpDisplay",
        header: "PCP Name",
        format: (row) =>
            formatLastFirstName(row.pcpLastName, row.pcpFirstName),
    },
];

/**
 * Notification queue table with loading / error / empty states, clickable data rows,
 * and an infinite-scroll sentinel row.
 *
 * @param {object} props
 * @param {import("../../useNotifications").UseNotificationsProps} props.useNotificationsHookData Full {@link useNotifications} bag;
 *   this component uses `notifications`, `isLoading`, `isError`, `fetchNextPage`, `hasNextPage`, `isFetchingNextPage`, and `queueFilter`.
 * @param {string | null} props.selectedNotificationId Row id pinned to the details sheet (highlight when open).
 * @param {boolean} props.detailsOpen When true, highlights the matching data row while the sheet is open.
 * @param {(row: Record<string, unknown> & { id: string }) => void} props.onRowActivate Opens / focuses details for `row`.
 * @returns {import('react').JSX.Element}
 */
const NotificationsList = ({
    useNotificationsHookData,
    selectedNotificationId,
    detailsOpen,
    onRowActivate,
}) => {
    const {
        notifications,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        queueFilter,
    } = useNotificationsHookData;

    const dataColumns = useMemo(() => {
        const timestampHeader =
            getSentOrScheduledTimestampColumnLabel(queueFilter);
        return [
            ...STATIC_DATA_COLUMNS,
            {
                key: "displayTimestamp",
                header: timestampHeader,
                format: (row) =>
                    formatNotificationLocaleDateTime(
                        getNotificationDisplayTimestamp(row),
                    ),
            },
        ];
    }, [queueFilter]);

    const { ref: loadMoreRef, inView } = useInView({
        rootMargin: "120px",
        threshold: 0,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const showNotificationRows = !isLoading && !isError;

    const showEmpty =
        showNotificationRows && notifications.length === 0 && !hasNextPage;

    const showLoadMoreRow = showNotificationRows && hasNextPage;

    /**
     * @param {import('react').KeyboardEvent} event
     * @param {Record<string, unknown> & { id: string }} row
     */
    const handleDataRowKeyDown = (event, row) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onRowActivate(row);
        }
    };

    const columnCount = dataColumns.length;

    return (
        <div className="notifications-list__table-region">
            <div className="notifications-list__scroll">
                <table className="notifications-list__table">
                    <thead>
                        <tr>
                            {dataColumns.map(({ key, header }) => (
                                <th key={key} scope="col">
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr className="notifications-list__status-row">
                                <td colSpan={columnCount}>
                                    <span
                                        className="loading-with-label"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        Loading notifications
                                        <span
                                            className="loader loader--inline"
                                            aria-hidden="true"
                                        />
                                    </span>
                                </td>
                            </tr>
                        )}
                        {isError && (
                            <tr className="notifications-list__status-row notifications-list__status-row--error">
                                <td colSpan={columnCount}>
                                    Error loading notifications.
                                </td>
                            </tr>
                        )}
                        {showEmpty && (
                            <tr className="notifications-list__empty-row">
                                <td colSpan={columnCount}>
                                    No notifications to display.
                                </td>
                            </tr>
                        )}
                        {showNotificationRows &&
                            notifications.map((row) => {
                                const isSelected =
                                    detailsOpen &&
                                    selectedNotificationId === row.id;

                                return (
                                    <tr
                                        key={String(row.id)}
                                        className={
                                            isSelected
                                                ? "notifications-list__data-row notifications-list__data-row--selected"
                                                : "notifications-list__data-row"
                                        }
                                        tabIndex={0}
                                        onClick={() => onRowActivate(row)}
                                        onKeyDown={(event) =>
                                            handleDataRowKeyDown(event, row)
                                        }
                                        aria-label="View notification details"
                                    >
                                        {dataColumns.map(({ key, format }) => (
                                            <td key={key}>{format(row)}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        {showLoadMoreRow && (
                            <tr
                                ref={loadMoreRef}
                                className="notifications-list__load-more-row"
                                aria-hidden={!isFetchingNextPage}
                            >
                                <td colSpan={columnCount}>
                                    {isFetchingNextPage ? (
                                        <div
                                            className="notifications-list__load-more-status"
                                            role="status"
                                            aria-live="polite"
                                            aria-label="Loading more notifications"
                                        >
                                            <span
                                                className="loader loader--inline"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    ) : (
                                        "\u00a0"
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default NotificationsList;
